import { useState } from "react";
import { Eye, FileDown, CheckCircle2, Undo2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { viewFromApi } from "@/components/FileViewer";

export const SEND_STATUS = (s) => {
  if (s.status === "completed") return s.review_status === "approved" ? ["Approved", "text-emerald-700", "bg-emerald-100 text-emerald-800"] : ["Signed", "text-emerald-700", "bg-emerald-100 text-emerald-800"];
  if (s.status === "awaiting_second") return ["Awaiting 2nd signature", "text-sky-700", "bg-sky-100 text-sky-800"];
  if (s.correction_note) return ["Returned for correction", "text-orange-700", "bg-orange-100 text-orange-800"];
  return ["Pending", "text-amber-700", "bg-amber-100 text-amber-800"];
};

export const viewSend = (s) => viewFromApi(api, `/forms/sends/${s.id}/view`, { title: s.form_title || "Form", type: "text/html" });

export const downloadSendPdf = async (s) => {
  try {
    const res = await api.get(`/forms/sends/${s.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = url; a.download = `${(s.form_title || "form").replace(/[^a-z0-9]+/gi, "-")}.pdf`; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed"); }
};

const btn = "inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100 transition disabled:opacity-50";

/** View / PDF / Approve / Return-for-correction controls for one form send (sender side). */
export function FormSendActions({ send, onChanged, compact = false }) {
  const [ret, setRet] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const signed = send.status !== "pending";

  const approve = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/forms/sends/${send.id}/approve`); toast.success("Form approved."); onChanged?.(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not approve"); }
    finally { setBusy(false); }
  };
  const doReturn = async () => {
    if (note.trim().length < 3) { toast.error("Tell the parent what needs to be corrected."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/forms/sends/${send.id}/return`, { note: note.trim() });
      toast.success(`Returned to the parent for correction${data.emailed ? " · email sent" : ""}.`);
      setRet(false); setNote(""); onChanged?.(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not return the form"); }
    finally { setBusy(false); }
  };

  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap justify-end" data-testid={`send-actions-${send.id}`}>
      <button onClick={() => viewSend(send)} data-testid={`send-view-${send.id}`} title={signed ? "Review the form" : "View the (unsigned) form"} className={btn}><Eye className="h-3 w-3" /> {compact ? "" : "View"}</button>
      {signed && <button onClick={() => downloadSendPdf(send)} data-testid={`send-pdf-${send.id}`} title="Download PDF" className={btn}><FileDown className="h-3 w-3" /> {compact ? "" : "PDF"}</button>}
      {send.status === "completed" && send.review_status !== "approved" && (
        <button onClick={approve} disabled={busy} data-testid={`send-approve-${send.id}`} title="Reviewed — everything is correct" className={`${btn} border-emerald-200 text-emerald-700 hover:bg-emerald-50`}><CheckCircle2 className="h-3 w-3" /> Approve</button>
      )}
      {signed && (
        <button onClick={() => setRet(true)} disabled={busy} data-testid={`send-return-${send.id}`} title="Send back to the parent for correction" className={`${btn} border-orange-200 text-orange-700 hover:bg-orange-50`}><Undo2 className="h-3 w-3" /> Return</button>
      )}
      <Dialog open={ret} onOpenChange={(o) => !o && setRet(false)}>
        <DialogContent className="sm:max-w-md" data-testid="return-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Undo2 className="h-5 w-5 text-orange-600" /> Return for correction</DialogTitle>
            <DialogDescription>{send.form_title} · {send.student_name}. The parent keeps their previous answers, fixes what you list here and signs again. Existing signatures are cleared.</DialogDescription>
          </DialogHeader>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="e.g. Phone number is missing and the address doesn't match the utility bill." data-testid="return-form-note"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary" />
          <DialogFooter>
            <button onClick={doReturn} disabled={busy} data-testid="return-form-confirm" className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />} Return to parent
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </span>
  );
}
